/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import type { ThemeMode } from '../../types';
import { applyV1ComponentColorUpgrades } from './upgrade-v1-component-colors';
import { applyRenamedV2Keys, applyV1ColorUpgrades } from './upgrade-v1-primitive-and-semantic-colors';

/**
 * Resolves a theme's colors against the v2 token set, returning a new map.
 * Colors the app has overridden are preserved; only untouched v1 defaults are
 * upgraded, which is what `v1Colors` is compared against to detect.
 *
 * Pass order is load-bearing:
 *
 *   1. primitive/semantic cross-key upgrades, override-checked against the
 *      untouched `themeColors`;
 *   2. component-color upgrades, override-checked against the result of (1);
 *   3. the v2 rename table last, so it wins on conflicts, with collision
 *      detection against the untouched `themeColors`.
 *
 * Each pass writes into one accumulator; running the three upgraders as
 * standalone calls would copy the ~1050-key map four times instead of once.
 */
export function mergeV2Colors(themeColors: Record<string, unknown>, v1Colors: Record<string, unknown> | undefined, v2Colors: Record<string, string>, themeMode: ThemeMode | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = Object.assign({}, themeColors);
  if (v1Colors != null) {
    applyV1ColorUpgrades(out, themeColors, v1Colors, v2Colors);
    applyV1ComponentColorUpgrades(out, v1Colors, v2Colors, themeMode);
  }
  applyRenamedV2Keys(out, themeColors, v2Colors);
  return out;
}